export const LENS_BOOK_SCOPE_QUERY_KEYS = Object.freeze({
  bookId: 'bookId',
  shelfId: 'shelfId',
});

export const LENS_BOOK_SCOPE_STORAGE_KEYS = Object.freeze({
  bookId: 'cleic.lens.activeBookId',
  shelfId: 'cleic.lens.activeShelfId',
  basis: 'cleic.lens.bookScopeBasis',
});

const VALID_BASIS = new Set([
  'ledger-url-handoff',
  'lens-selector',
  'lens-default-book-id',
]);

const NO_ACTIVE_SCOPE = Object.freeze({
  bookId: null,
  shelfId: null,
  basis: null,
});

export function normalizeBookScopeId(value) {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > 256) return null;
  return trimmed;
}

export function normalizeBookScopeBasis(value, fallback = 'lens-selector') {
  const raw = typeof value === 'string' ? value.trim() : '';
  return VALID_BASIS.has(raw) ? raw : fallback;
}

function searchParamsFrom(search = '') {
  return new URLSearchParams(String(search || '').replace(/^\?/, ''));
}

export function readBookScopeFromSearch(search = '') {
  const params = searchParamsFrom(search);
  const hasBookParam = params.has(LENS_BOOK_SCOPE_QUERY_KEYS.bookId);
  const hasShelfParam = params.has(LENS_BOOK_SCOPE_QUERY_KEYS.shelfId);
  return {
    hasUrlScope: hasBookParam || hasShelfParam,
    hasBookParam,
    hasShelfParam,
    bookId: normalizeBookScopeId(params.get(LENS_BOOK_SCOPE_QUERY_KEYS.bookId)),
    shelfId: normalizeBookScopeId(params.get(LENS_BOOK_SCOPE_QUERY_KEYS.shelfId)),
    basis: hasBookParam || hasShelfParam ? 'ledger-url-handoff' : null,
  };
}

export function stripBookScopeParamsFromLocation(location) {
  const pathname = location?.pathname || '/';
  const hash = location?.hash || '';
  const params = searchParamsFrom(location?.search || '');
  params.delete(LENS_BOOK_SCOPE_QUERY_KEYS.bookId);
  params.delete(LENS_BOOK_SCOPE_QUERY_KEYS.shelfId);
  const nextSearch = params.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ''}${hash}`;
}

export function readStoredLensBookScope(storage) {
  if (!storage) return { bookId: null, shelfId: null, basis: null };
  const bookId = normalizeBookScopeId(storage.getItem(LENS_BOOK_SCOPE_STORAGE_KEYS.bookId));
  const shelfId = normalizeBookScopeId(storage.getItem(LENS_BOOK_SCOPE_STORAGE_KEYS.shelfId));
  const hasScope = !!(shelfId || bookId);
  return {
    bookId,
    shelfId,
    basis: hasScope
      ? normalizeBookScopeBasis(
          storage.getItem(LENS_BOOK_SCOPE_STORAGE_KEYS.basis),
          'lens-selector',
        )
      : null,
  };
}

export function persistLensBookScope(selection, storage) {
  if (!storage) return selection;
  const bookId = normalizeBookScopeId(selection?.bookId);
  const shelfId = normalizeBookScopeId(selection?.shelfId);
  const basis = normalizeBookScopeBasis(selection?.basis, 'lens-selector');
  const hasScope = !!(shelfId || bookId);

  if (bookId) storage.setItem(LENS_BOOK_SCOPE_STORAGE_KEYS.bookId, bookId);
  else storage.removeItem(LENS_BOOK_SCOPE_STORAGE_KEYS.bookId);

  if (shelfId) storage.setItem(LENS_BOOK_SCOPE_STORAGE_KEYS.shelfId, shelfId);
  else storage.removeItem(LENS_BOOK_SCOPE_STORAGE_KEYS.shelfId);

  if (hasScope) storage.setItem(LENS_BOOK_SCOPE_STORAGE_KEYS.basis, basis);
  else storage.removeItem(LENS_BOOK_SCOPE_STORAGE_KEYS.basis);

  return { bookId, shelfId, basis: hasScope ? basis : null };
}

export function hydrateLensBookScopeFromLocation({
  location,
  storage,
  replaceUrl,
} = {}) {
  const urlScope = readBookScopeFromSearch(location?.search || '');

  if (urlScope.hasShelfParam && urlScope.shelfId) {
    const selection = persistLensBookScope({
      bookId: null,
      shelfId: urlScope.shelfId,
      basis: 'ledger-url-handoff',
    }, storage);
    if (urlScope.hasUrlScope && typeof replaceUrl === 'function') {
      replaceUrl(stripBookScopeParamsFromLocation(location));
    }
    return selection;
  }

  if (urlScope.hasBookParam && urlScope.bookId) {
    const selection = persistLensBookScope({
      bookId: urlScope.bookId,
      shelfId: null,
      basis: 'ledger-url-handoff',
    }, storage);
    if (urlScope.hasUrlScope && typeof replaceUrl === 'function') {
      replaceUrl(stripBookScopeParamsFromLocation(location));
    }
    return selection;
  }

  if (urlScope.hasUrlScope && typeof replaceUrl === 'function') {
    replaceUrl(stripBookScopeParamsFromLocation(location));
  }

  // Stored scope is recent/history metadata only. It must never become the
  // active Lens scope on bootstrap without an explicit URL handoff.
  return { ...NO_ACTIVE_SCOPE };
}

export function hydrateLensBookScopeFromBrowser() {
  if (typeof window === 'undefined') return { bookId: null, shelfId: null, basis: null };
  return hydrateLensBookScopeFromLocation({
    location: window.location,
    storage: window.localStorage,
    replaceUrl: (nextUrl) => window.history.replaceState(null, '', nextUrl),
  });
}

export function findShelfIdForBook(catalog, bookId) {
  const normalizedBookId = normalizeBookScopeId(bookId);
  if (!normalizedBookId) return null;
  const links = Array.isArray(catalog?.bookLinks) ? catalog.bookLinks : [];
  return links.find((link) => link?.bookId === normalizedBookId)?.shelfId ?? null;
}

export function findShelfScopeExpansion(catalog, shelfId) {
  const normalizedShelfId = normalizeBookScopeId(shelfId);
  if (!normalizedShelfId) return null;
  const expansions = Array.isArray(catalog?.scopeExpansions) ? catalog.scopeExpansions : [];
  return expansions.find((expansion) => expansion?.shelfId === normalizedShelfId) ?? null;
}

export function pickBookIdForShelf(catalog, shelfId) {
  const normalizedShelfId = normalizeBookScopeId(shelfId);
  if (!normalizedShelfId) return null;
  const links = Array.isArray(catalog?.bookLinks) ? catalog.bookLinks : [];
  return links.find((link) => link?.shelfId === normalizedShelfId)?.bookId ?? null;
}
